import { WIZARD_QUESTION_IDS } from '../constants.js';
import { validateOperatingSchedule } from './schedule.js';
import type {
  AnswerSource,
  AnswerStatus,
  ConditionalAnswer,
  ConnectedAnswers,
  FilledConditionalAnswer,
  LocationGrounding,
  OperatingSchedule,
  TimePeriod,
  WizardStepId,
  WorldDraft,
} from './types.js';
import {
  CONDITIONAL_ANSWER_SCHEMA_VERSION,
  isNonBlankValue,
} from './types.js';

export type ApplyAnswerResult =
  | { ok: true; draft: WorldDraft }
  | { ok: false; error: string; draft: WorldDraft };

/** Builder steps in dependency order (notes §5). */
export const WIZARD_STEP_DEPENDENCY_ORDER = [
  'location-grounding',
  'connected',
  'time-period',
  'operating-hours',
  'review',
] as const;

export type BuilderDependencyStep = (typeof WIZARD_STEP_DEPENDENCY_ORDER)[number];

/** Question ids grouped by wizard step for downstream stale propagation. */
export const WIZARD_STEP_FIELD_IDS: Record<BuilderDependencyStep, readonly string[]> = {
  'location-grounding': [
    WIZARD_QUESTION_IDS.groundingName,
    WIZARD_QUESTION_IDS.groundingRegion,
    WIZARD_QUESTION_IDS.groundingLanguage,
  ],
  connected: [
    WIZARD_QUESTION_IDS.connectedLocationType,
    WIZARD_QUESTION_IDS.connectedDescription,
    WIZARD_QUESTION_IDS.connectedCrowd,
    WIZARD_QUESTION_IDS.connectedRhythm,
  ],
  'time-period': [
    WIZARD_QUESTION_IDS.periodPresent,
    WIZARD_QUESTION_IDS.periodPeriod,
    WIZARD_QUESTION_IDS.periodRationale,
  ],
  'operating-hours': ['schedule'],
  review: [],
};

const QUESTION_TO_STEP = new Map<string, BuilderDependencyStep>(
  Object.entries(WIZARD_STEP_FIELD_IDS).flatMap(([step, ids]) =>
    ids.map((id) => [id, step as BuilderDependencyStep]),
  ),
);

export interface BlankFieldRef {
  fieldId: string;
  label: string;
}

export interface ReviewValidationResult {
  ok: boolean;
  blankCount: number;
  blanks: BlankFieldRef[];
  errors: string[];
}

export interface LaunchValidationResult {
  ok: boolean;
  errors: string[];
}

function cloneAnswers(
  answers: Record<string, ConditionalAnswer>,
): Record<string, ConditionalAnswer> {
  return Object.fromEntries(
    Object.entries(answers).map(([k, v]) => [k, { ...v }]),
  );
}

export function wizardStepForQuestion(questionId: string): BuilderDependencyStep | null {
  return QUESTION_TO_STEP.get(questionId) ?? null;
}

/** Default upstream deps: all field ids from prior builder steps. */
export function defaultDependenciesForQuestion(questionId: string): string[] {
  const step = wizardStepForQuestion(questionId);
  if (!step) return [];
  const stepIndex = WIZARD_STEP_DEPENDENCY_ORDER.indexOf(step);
  if (stepIndex <= 0) return [];
  const deps: string[] = [];
  for (let i = 0; i < stepIndex; i += 1) {
    deps.push(...WIZARD_STEP_FIELD_IDS[WIZARD_STEP_DEPENDENCY_ORDER[i]!]);
  }
  return deps;
}

function downstreamSteps(fromStep: BuilderDependencyStep): BuilderDependencyStep[] {
  const index = WIZARD_STEP_DEPENDENCY_ORDER.indexOf(fromStep);
  if (index < 0) return [];
  return WIZARD_STEP_DEPENDENCY_ORDER.slice(index + 1);
}

function downstreamQuestionIds(fromStep: BuilderDependencyStep): string[] {
  return downstreamSteps(fromStep).flatMap((step) => [...WIZARD_STEP_FIELD_IDS[step]]);
}

/** Collect question ids that transitively depend on `rootId` (explicit deps only). */
export function collectDependentQuestionIds(
  answers: Record<string, ConditionalAnswer>,
  rootId: string,
): Set<string> {
  const dependents = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const [id, answer] of Object.entries(answers)) {
      if (dependents.has(id)) continue;
      const deps = answer.dependencies ?? [];
      if (deps.includes(rootId) || deps.some((d) => dependents.has(d))) {
        dependents.add(id);
        changed = true;
      }
    }
  }
  return dependents;
}

function shouldPreserveOnStale(answer: ConditionalAnswer): boolean {
  return answer.status === 'unanswered' || answer.status === 'skipped';
}

/** Mark downstream answers stale; never skipped rhythm unless caller clears skip first. */
export function markDependentsStale(
  answers: Record<string, ConditionalAnswer>,
  changedQuestionId: string,
): Record<string, ConditionalAnswer> {
  const step = wizardStepForQuestion(changedQuestionId);
  const staleIds = new Set(collectDependentQuestionIds(answers, changedQuestionId));
  if (step) {
    for (const id of downstreamQuestionIds(step)) {
      staleIds.add(id);
    }
  }
  if (staleIds.size === 0) return answers;

  const next = cloneAnswers(answers);
  for (const id of staleIds) {
    const answer = next[id];
    if (!answer || shouldPreserveOnStale(answer)) continue;
    next[id] = { ...answer, status: 'stale' };
  }
  return next;
}

export function canSilentlyOverwriteAnswer(answer: ConditionalAnswer): boolean {
  return answer.status !== 'confirmed';
}

export interface ApplyDraftAnswerInput {
  questionId: string;
  value: unknown;
  status: AnswerStatus;
  source: AnswerSource | null;
  dependencies?: string[];
  schemaVersion?: string;
  updatedAt?: string;
  allowConfirmedOverwrite?: boolean;
}

function isBuilderUpstreamEdit(questionId: string): boolean {
  const step = wizardStepForQuestion(questionId);
  return step !== null && step !== 'review';
}

function buildConditionalAnswer(
  input: ApplyDraftAnswerInput,
  existing: ConditionalAnswer | undefined,
  mergedDeps: string[],
  now: string,
): ConditionalAnswer {
  const base = {
    questionId: input.questionId,
    schemaVersion: input.schemaVersion ?? existing?.schemaVersion ?? CONDITIONAL_ANSWER_SCHEMA_VERSION,
    dependencies: mergedDeps,
    updatedAt: now,
  };
  switch (input.status) {
    case 'unanswered':
      return { ...base, status: 'unanswered', value: null, source: input.source };
    case 'skipped':
      return { ...base, status: 'skipped', value: { skipped: true }, source: input.source };
    case 'stale':
      return { ...base, status: 'stale', value: input.value, source: input.source };
    case 'answered':
    case 'suggested':
    case 'confirmed':
      return {
        ...base,
        status: input.status,
        value: input.value,
        source: input.source ?? existing?.source ?? 'user',
      };
    default:
      return { ...base, status: 'unanswered', value: null, source: input.source };
  }
}

/** Apply an answer patch; upstream edits mark only downstream stale and clear review acceptance. */
export function applyDraftAnswer(
  draft: WorldDraft,
  input: ApplyDraftAnswerInput,
): ApplyAnswerResult {
  const existing = draft.answers[input.questionId];
  const now = input.updatedAt ?? new Date().toISOString();

  if (existing && !canSilentlyOverwriteAnswer(existing) && !input.allowConfirmedOverwrite) {
    const sameValue = JSON.stringify(existing.value) === JSON.stringify(input.value);
    if (!sameValue || input.status !== 'confirmed') {
      return {
        ok: false,
        error: `Answer "${input.questionId}" is confirmed and cannot be silently overwritten`,
        draft,
      };
    }
  }

  const prevValue = existing?.value;
  const valueChanged =
    existing !== undefined &&
    JSON.stringify(prevValue) !== JSON.stringify(input.value);

  const mergedDeps = [
    ...new Set([
      ...defaultDependenciesForQuestion(input.questionId),
      ...(input.dependencies ?? existing?.dependencies ?? []),
    ]),
  ];

  const nextAnswer = buildConditionalAnswer(input, existing, mergedDeps, now);

  let answers = { ...draft.answers, [input.questionId]: nextAnswer };
  let reviewAccepted = draft.reviewAccepted;
  if (valueChanged) {
    answers = markDependentsStale(answers, input.questionId);
    if (isBuilderUpstreamEdit(input.questionId)) {
      reviewAccepted = false;
    }
  }

  return {
    ok: true,
    draft: { ...draft, answers, reviewAccepted, updatedAt: now },
  };
}

/** Invalidate downstream when aggregate step data changes (location, connected, etc.). */
export function invalidateDraftOnStepEdit(
  draft: WorldDraft,
  step: BuilderDependencyStep,
  now = new Date().toISOString(),
): WorldDraft {
  const anchorId = WIZARD_STEP_FIELD_IDS[step][0];
  if (!anchorId) {
    return { ...draft, reviewAccepted: false, updatedAt: now };
  }
  return {
    ...draft,
    answers: markDependentsStale(draft.answers, anchorId),
    reviewAccepted: false,
    updatedAt: now,
  };
}

function groundingBlanks(location: LocationGrounding | null): BlankFieldRef[] {
  const blanks: BlankFieldRef[] = [];
  if (!location || !isNonBlankValue(location.name)) {
    blanks.push({ fieldId: WIZARD_QUESTION_IDS.groundingName, label: 'Name / label / title' });
  }
  if (!location || !isNonBlankValue(location.region)) {
    blanks.push({ fieldId: WIZARD_QUESTION_IDS.groundingRegion, label: 'City, region, and country' });
  }
  return blanks;
}

function connectedBlanks(connected: ConnectedAnswers | null): BlankFieldRef[] {
  if (!connected) {
    return [
      { fieldId: WIZARD_QUESTION_IDS.connectedLocationType, label: 'Type of location' },
      { fieldId: WIZARD_QUESTION_IDS.connectedDescription, label: 'Description' },
    ];
  }
  const blanks: BlankFieldRef[] = [];
  if (!isNonBlankValue(connected.locationType)) {
    blanks.push({ fieldId: WIZARD_QUESTION_IDS.connectedLocationType, label: 'Type of location' });
  }
  if (!isNonBlankValue(connected.description)) {
    blanks.push({ fieldId: WIZARD_QUESTION_IDS.connectedDescription, label: 'Description' });
  }
  if (
    connected.crowd?.type === 'other' &&
    !isNonBlankValue(connected.crowd.otherDescription)
  ) {
    blanks.push({
      fieldId: WIZARD_QUESTION_IDS.connectedCrowd,
      label: 'Describe who typically comes here',
    });
  }
  if (
    !connected.rhythm.skipped &&
    connected.rhythm.type === 'other' &&
    !isNonBlankValue(connected.rhythm.otherDescription)
  ) {
    blanks.push({
      fieldId: WIZARD_QUESTION_IDS.connectedRhythm,
      label: 'Describe the daily rhythm',
    });
  }
  return blanks;
}

function periodBlanks(timePeriod: TimePeriod | null): BlankFieldRef[] {
  const present = timePeriod?.present?.trim() ?? '';
  const period = timePeriod?.period?.trim() ?? '';
  if (present.length > 0 || period.length > 0) return [];
  return [{ fieldId: WIZARD_QUESTION_IDS.periodPresent, label: 'Present or Period' }];
}

function scheduleBlanks(schedule: OperatingSchedule | null): BlankFieldRef[] {
  if (!schedule) {
    return [{ fieldId: 'schedule', label: 'Operating/Working hours' }];
  }
  const result = validateOperatingSchedule(schedule);
  if (result.valid) return [];
  return [{ fieldId: 'schedule', label: 'Operating/Working hours' }];
}

/** Count required blank fields for review blank-check (notes §10). Rhythm skip is not blank. */
export function countRequiredBlankFields(draft: WorldDraft): {
  count: number;
  blanks: BlankFieldRef[];
} {
  const blanks = [
    ...groundingBlanks(draft.location),
    ...connectedBlanks(draft.connected),
    ...periodBlanks(draft.timePeriod),
    ...scheduleBlanks(draft.schedule),
  ];
  return { count: blanks.length, blanks };
}

/** Review continue gate: acceptance required; stale blocks; blanks reported separately. */
export function validateReviewContinue(draft: WorldDraft): ReviewValidationResult {
  const { count, blanks } = countRequiredBlankFields(draft);
  const errors: string[] = [];
  if (!draft.reviewAccepted) {
    errors.push('Review acceptance is required before continuing.');
  }
  if (draftHasBlockingStaleAnswers(draft)) {
    errors.push('Stale answers must be refreshed before continuing.');
  }
  return {
    ok: errors.length === 0,
    blankCount: count,
    blanks,
    errors,
  };
}

/** Launch gate: required fields valid, non-stale, review accepted, ≥1 character hook. */
export function validateLaunchWorld(draft: WorldDraft): LaunchValidationResult {
  const review = validateReviewContinue(draft);
  const errors = [...review.errors];
  if (review.blankCount > 0) {
    errors.push(`you have ${review.blankCount} blank unfilled questions`);
  }
  if (draft.characterDrafts.length < 1) {
    errors.push('At least one character is required to launch.');
  }
  return { ok: errors.length === 0, errors };
}

/** Answers eligible for a confirmed world snapshot (excludes stale/unanswered). */
export function confirmedAnswersForSnapshot(
  answers: Record<string, ConditionalAnswer>,
): Record<string, FilledConditionalAnswer & { status: 'confirmed' }> {
  return Object.fromEntries(
    Object.entries(answers).filter(
      (entry): entry is [string, FilledConditionalAnswer & { status: 'confirmed' }] =>
        entry[1].status === 'confirmed',
    ),
  );
}

/** True when any answer is stale — blocks snapshot launch. */
export function draftHasBlockingStaleAnswers(draft: WorldDraft): boolean {
  return Object.values(draft.answers).some((a) => a.status === 'stale');
}

/** Whether snapshot serialization may proceed for this draft. */
export function canBuildLaunchSnapshot(draft: WorldDraft): boolean {
  return validateLaunchWorld(draft).ok;
}

/** Clear review acceptance when returning to edit upstream steps. */
export function clearReviewAcceptance(draft: WorldDraft, now = new Date().toISOString()): WorldDraft {
  return { ...draft, reviewAccepted: false, updatedAt: now };
}

/** Mark wizard step for saved-draft resume (pure). */
export function markSavedDraftStep(
  draft: WorldDraft,
  resumeStep: WizardStepId,
  now = new Date().toISOString(),
): WorldDraft {
  return { ...draft, wizardStep: resumeStep, updatedAt: now };
}
